/**
 * +idle: the hero standing layer (pure, no three.js, no allocation per frame).
 *
 * Heroes used to stand like mannequins: legs locked together, arms straight out in an A, weapons
 * jutting forward out of the fists, one sine wave nodding the head and nothing else moving. This
 * layer replaces that with offsets in the clip channel space (anim/clips.ts), added on top of the
 * base locomotion pose by `poseVrm`:
 *
 *  - a relaxed per-class rest pose that holds the weapon properly (sword and shield low, staff
 *    upright beside the body, bow lowered, wand and orb ready);
 *  - contrapposto: the weight sits on one leg (hips shifted over it, the free hip dropped, the free
 *    knee soft, the shoulders countering) and moves to the other leg every 6–14 s;
 *  - a two-bone leg IK that keeps both feet planted while the hips shift, drop or twist, and steps
 *    a foot (lift arc) when the stance changes, so nothing slides;
 *  - breathing in the chest and shoulders (an asymmetric inhale / exhale), value-noise micro-motion
 *    instead of sines, occasional look-arounds (neck + head) with a blink on the glance, and random
 *    blinks (sometimes doubled);
 *  - a combat-ready variant (lower, staggered stance, weapon up), held while in combat and for a
 *    few seconds after any ability clip;
 *  - class fidgets every 8–15 s (shield adjust, dagger twirl, staff lean, orb gaze, bow check, a
 *    glance at the sky, a neck stretch);
 *  - a per-hero seed so a party never moves in sync.
 *
 * Blend weights (`weights`): run + idle + combat = 1. Cast poses are clips with their own arm
 * weights; the idle arms give way to `armL` / `armR` exactly like the old guard / staff hold did.
 */
import { BONE_INDEX, type Clip, type ClipBone, NCH, type PoseDef, ch, defineClip, evalClip } from './clips';

// ---------------------------------------------------------------------------
// Small deterministic helpers
// ---------------------------------------------------------------------------

/** Deterministic PRNG (mulberry32): 0..1. */
export function rng(seed: number): () => number {
  let a = (Math.floor(seed * 0x7fffffff) ^ 0x9e3779b9) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash1(i: number): number {
  let h = Math.imul(i | 0, 0x27d4eb2d) ^ 0x165667b1;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return ((h >>> 0) / 4294967295) * 2 - 1;
}

/** Smooth 1D value noise in -1..1 (C1, cheap; no allocation). */
export function noise1(x: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const u = f * f * (3 - 2 * f);
  const a = hash1(i);
  return a + (hash1(i + 1) - a) * u;
}

const clamp = (x: number, a: number, b: number): number => (x < a ? a : x > b ? b : x);
const smooth = (x: number): number => {
  const t = clamp(x, 0, 1);
  return t * t * (3 - 2 * t);
};
const approach = (cur: number, target: number, rate: number, dt: number): number => cur + (target - cur) * (1 - Math.exp(-rate * dt));

// ---------------------------------------------------------------------------
// Styles (per class key: warden, blademaster, stormcaller, lightbinder, ranger)
// ---------------------------------------------------------------------------

/** A foot's resting spot relative to its rest-pose spot (yd): [outward +, forward +]. */
export type FootSpot = readonly [number, number];

export interface IdleStyleDef {
  /** Relaxed standing offsets (arms, hands, torso). */
  rest: PoseDef;
  /** Combat-ready offsets (weapon up, torso). */
  combat: PoseDef;
  /** Relaxed feet [left, right]. */
  feet: readonly [FootSpot, FootSpot];
  /** Combat feet [left, right] (the stance steps into them). */
  combatFeet: readonly [FootSpot, FootSpot];
  /** Combat hip drop (yd) and pelvis twist (rad, + = left). */
  crouch: number;
  combatTwist: number;
  /** Combat bounce on the knees (yd amplitude; 0 = none) and its rate (Hz). */
  bounce?: number;
  bounceHz?: number;
  /** Contrapposto amount 0..1 (1 = the full hip shift / tilt). */
  sway: number;
  /** Share of the rest arm offsets kept while running (a staff stays upright). */
  runKeep?: number;
  fidgets: readonly Clip[];
}

export interface IdleStyle extends IdleStyleDef {
  readonly restV: Float32Array;
  readonly combatV: Float32Array;
}

/** Pose definition → channel vector (missing = 0). */
export function poseVector(p: PoseDef, out = new Float32Array(NCH)): Float32Array {
  out.fill(0);
  for (const [bone, vals] of Object.entries(p) as [ClipBone, readonly number[]][]) {
    const b = BONE_INDEX[bone];
    if (b === undefined) throw new Error(`idle: unknown bone ${bone}`);
    for (let a = 0; a < 3 && a < vals.length; a++) out[b * 3 + a] = vals[a]!;
  }
  return out;
}

// Fidgets (keyed offsets on top of the rest pose; every clip starts and ends at zero).
const fidget = (id: string, duration: number, keys: Parameters<typeof defineClip>[0]['keys']): Clip => defineClip({ id: `idle.${id}`, duration, keys, blendIn: 0, blendOut: 0 });

/** Look up at the sky for a moment. */
const SKY = fidget('sky', 3.2, [
  { t: 0.7, ease: 'inOut', pose: { neck: [-0.12], head: [-0.3, 0.12, -0.05], chest: [-0.04] } },
  { t: 2.2, ease: 'linear', pose: { neck: [-0.13], head: [-0.32, 0.18, -0.07], chest: [-0.045] } },
]);
/** Neck stretch: tilt to one side, then the other. */
const NECK = fidget('neck', 2.8, [
  { t: 0.55, ease: 'inOut', pose: { head: [0.05, 0, 0.32], neck: [0, 0, 0.12] } },
  { t: 0.9, ease: 'linear', pose: { head: [0.06, 0, 0.34], neck: [0, 0, 0.13] } },
  { t: 1.7, ease: 'inOut', pose: { head: [0.05, 0, -0.3], neck: [0, 0, -0.1] } },
  { t: 2.0, ease: 'linear', pose: { head: [0.06, 0, -0.32], neck: [0, 0, -0.11] } },
]);
/** Look back over the left shoulder. */
const OVER_SHOULDER = fidget('over', 2.6, [
  { t: 0.6, ease: 'inOut', pose: { spine: [0, 0.12], chest: [0, 0.14], neck: [0, 0.25], head: [0.05, 0.5, 0.05] } },
  { t: 1.6, ease: 'linear', pose: { spine: [0, 0.13], chest: [0, 0.15], neck: [0, 0.27], head: [0.06, 0.55, 0.05] } },
]);

/** Warrior: lift and resettle the shield, roll the shoulder. */
const SHIELD_ADJUST = fidget('shield', 2.4, [
  { t: 0.45, ease: 'inOut', pose: { leftUpperArm: [0.45, 0.2, 0.1], leftLowerArm: [0.55], leftHand: [0.3], head: [0.12, 0.25], chest: [0, 0.08] } },
  { t: 0.8, ease: 'back', overshoot: 1.2, pose: { leftUpperArm: [0.25, 0.1, 0.05], leftLowerArm: [0.3], leftHand: [-0.15], head: [0.1, 0.2] } },
  { t: 1.4, ease: 'inOut', pose: { leftUpperArm: [0.05, 0, 0.12], head: [0.04, 0.05], chest: [-0.03] } },
]);
/** Warrior: raise the sword and check the edge. */
const SWORD_CHECK = fidget('swordcheck', 3.0, [
  { t: 0.7, ease: 'inOut', pose: { rightUpperArm: [0.75, 0.25, 0], rightLowerArm: [1.1], rightHand: [0.4, -0.3], head: [0.18, -0.25], chest: [0.02, -0.08] } },
  { t: 1.5, ease: 'inOut', pose: { rightUpperArm: [0.8, 0.3, 0], rightLowerArm: [1.15], rightHand: [-0.5, -0.3], head: [0.2, -0.28], chest: [0.02, -0.08] } },
  { t: 2.2, ease: 'inOut', pose: { rightUpperArm: [0.6, 0.2, 0], rightLowerArm: [0.9], rightHand: [0.1, -0.2], head: [0.12, -0.15] } },
]);
/** Rogue: twirl a dagger (a full roll of the hand) and catch it. */
const TWIRL = fidget('twirl', 1.7, [
  { t: 0.3, ease: 'out', pose: { rightUpperArm: [0.3, 0.1], rightLowerArm: [0.9], rightHand: [0, 0, 0], head: [0.1, -0.15] } },
  { t: 0.85, ease: 'linear', pose: { rightUpperArm: [0.35, 0.12], rightLowerArm: [1.0], rightHand: [Math.PI * 2], head: [0.12, -0.18] } },
  { t: 1.05, ease: 'back', overshoot: 1.4, pose: { rightUpperArm: [0.3, 0.1], rightLowerArm: [0.85], rightHand: [Math.PI * 2], head: [0.1, -0.15] } },
  { t: 1.7, ease: 'inOut', pose: { rightHand: [Math.PI * 2] } },
]);
/** Rogue: roll the shoulders, a little bounce. */
const LOOSEN = fidget('loosen', 1.8, [
  { t: 0.35, ease: 'inOut', pose: { chest: [-0.05, 0.1], leftUpperArm: [-0.1, 0, 0.15], rightUpperArm: [0.1, 0, 0.05], head: [0, 0.1, 0.1] } },
  { t: 0.8, ease: 'inOut', pose: { chest: [-0.05, -0.1], leftUpperArm: [0.1, 0, 0.05], rightUpperArm: [-0.1, 0, 0.15], head: [0, -0.1, -0.1] } },
  { t: 1.2, ease: 'inOut', pose: { chest: [0.02, 0] } },
]);
/** Mage: raise the orb hand and gaze into it. */
const ORB_GAZE = fidget('orb', 3.0, [
  { t: 0.7, ease: 'inOut', pose: { leftUpperArm: [0.45, 0.25, 0], leftLowerArm: [0.5], head: [0.28, 0.18, 0.05], neck: [0.08], chest: [0.03, 0.05] } },
  { t: 2.0, ease: 'linear', pose: { leftUpperArm: [0.5, 0.28, 0], leftLowerArm: [0.55], head: [0.3, 0.2, 0.08], neck: [0.08], chest: [0.03, 0.05] } },
]);
/** Mage: a little flourish of the wand. */
const WAND_FLICK = fidget('wand', 1.6, [
  { t: 0.35, ease: 'out', pose: { rightUpperArm: [0.4, 0.15], rightLowerArm: [0.9], rightHand: [0.6], head: [0.08, -0.15] } },
  { t: 0.75, ease: 'snap', pose: { rightUpperArm: [0.5, 0.3], rightLowerArm: [0.7], rightHand: [-0.4], head: [0.06, -0.18] } },
]);
/** Acolyte: lean on the staff (hips over to the staff side, head tilted). */
const STAFF_LEAN = fidget('lean', 3.4, [
  { t: 0.8, ease: 'inOut', pose: { spine: [0.02, 0, 0.1], chest: [0, 0, 0.05], head: [0, -0.05, -0.12], rightUpperArm: [0, 0, 0.1], rightLowerArm: [-0.15] } },
  { t: 2.4, ease: 'linear', pose: { spine: [0.02, 0, 0.11], chest: [0, 0, 0.06], head: [0, -0.08, -0.14], rightUpperArm: [0, 0, 0.1], rightLowerArm: [-0.15] } },
]);
/** Acolyte: a short prayer (free hand to the chest, head bowed). */
const PRAY = fidget('pray', 3.2, [
  { t: 0.7, ease: 'inOut', pose: { leftUpperArm: [0.35, 0.55, -0.1], leftLowerArm: [1.55], head: [0.32], neck: [0.1], chest: [0.04] } },
  { t: 2.3, ease: 'linear', pose: { leftUpperArm: [0.37, 0.57, -0.1], leftLowerArm: [1.6], head: [0.34], neck: [0.1], chest: [0.05] } },
]);
/** Ranger: lift the bow and pluck / check the string. */
const BOW_CHECK = fidget('bow', 2.8, [
  { t: 0.6, ease: 'inOut', pose: { leftUpperArm: [0.6, 0.35], leftLowerArm: [0.7], rightUpperArm: [0.5, 0.5], rightLowerArm: [1.3], head: [0.2, 0.15] } },
  { t: 1.0, ease: 'snap', pose: { leftUpperArm: [0.62, 0.37], leftLowerArm: [0.72], rightUpperArm: [0.45, 0.42], rightLowerArm: [1.15], head: [0.2, 0.15] } },
  { t: 1.9, ease: 'inOut', pose: { leftUpperArm: [0.5, 0.3], leftLowerArm: [0.6], rightUpperArm: [0.1], rightLowerArm: [0.2], head: [0.15, 0.1] } },
]);
/** Ranger: scan the horizon (left, then right). */
const SCAN = fidget('scan', 3.6, [
  { t: 0.8, ease: 'inOut', pose: { chest: [0, 0.15], neck: [0, 0.2], head: [-0.06, 0.45] } },
  { t: 1.4, ease: 'linear', pose: { chest: [0, 0.16], neck: [0, 0.21], head: [-0.07, 0.47] } },
  { t: 2.4, ease: 'inOut', pose: { chest: [0, -0.15], neck: [0, -0.2], head: [-0.06, -0.45] } },
  { t: 2.9, ease: 'linear', pose: { chest: [0, -0.16], neck: [0, -0.21], head: [-0.07, -0.47] } },
]);

/**
 * Per class styles. Arm channels: upper arm [raise forward, sweep across, raise sideways],
 * lower arm [elbow bend, forearm twist], hand [roll, yaw, pitch]. Tuned against the five hero
 * VRMs (debug-heroes.html?cols=warrior,rogue,mage,acolyte,ranger&rows=idle,combat).
 */
export const IDLE_STYLE_DEFS: Record<string, IdleStyleDef> = {
  default: {
    rest: { leftUpperArm: [0.06, 0, -0.16], rightUpperArm: [0.06, 0, -0.16], leftLowerArm: [0.22], rightLowerArm: [0.22] },
    combat: { spine: [0.08], leftUpperArm: [0.35, 0.2], rightUpperArm: [0.35, 0.2], leftLowerArm: [0.9], rightLowerArm: [0.9] },
    feet: [[0.03, 0], [0.03, 0]],
    combatFeet: [[0.08, 0.12], [0.1, -0.1]],
    crouch: 0.07,
    combatTwist: -0.15,
    sway: 1,
    fidgets: [SKY, NECK, OVER_SHOULDER],
  },
  warden: {
    rest: {
      leftUpperArm: [0.12, 0.05, -0.08],
      leftLowerArm: [0.35],
      leftHand: [0, 0, 0],
      rightUpperArm: [-0.12, 0, -0.14],
      rightLowerArm: [0.1],
      rightHand: [-0.2, -1.1, 0],
      chest: [-0.02],
    },
    combat: {
      spine: [0.14, 0.12],
      chest: [0.04, 0.05],
      head: [-0.12, -0.12],
      leftUpperArm: [0.42, 0.5, 0.0],
      leftLowerArm: [1.35],
      leftHand: [0.2],
      rightUpperArm: [0.12, 0.0, 0.25],
      rightLowerArm: [1.1],
      rightHand: [-0.2, -0.45, 0],
    },
    feet: [[0.05, 0.02], [0.05, -0.02]],
    combatFeet: [[0.1, 0.16], [0.12, -0.12]],
    crouch: 0.08,
    combatTwist: -0.22,
    sway: 0.8,
    fidgets: [SHIELD_ADJUST, SWORD_CHECK, NECK, SKY],
  },
  blademaster: {
    rest: {
      leftUpperArm: [-0.12, 0, -0.12],
      rightUpperArm: [-0.12, 0, -0.12],
      leftLowerArm: [0.1],
      rightLowerArm: [0.1],
      leftHand: [-0.2, -1.1, 0],
      rightHand: [-0.2, -1.1, 0],
      chest: [-0.01],
      head: [0.03],
    },
    combat: {
      spine: [0.2, 0.05],
      chest: [0.05],
      head: [-0.16],
      leftUpperArm: [0.32, 0.05, 0.12],
      rightUpperArm: [0.38, 0.05, 0.12],
      leftLowerArm: [0.95],
      rightLowerArm: [0.95],
      leftHand: [-0.2, -0.7],
      rightHand: [-0.2, -0.7],
    },
    feet: [[0.05, 0.03], [0.05, -0.03]],
    combatFeet: [[0.13, 0.14], [0.13, -0.12]],
    crouch: 0.12,
    combatTwist: -0.12,
    bounce: 0.014,
    bounceHz: 1.5,
    sway: 1,
    fidgets: [TWIRL, LOOSEN, OVER_SHOULDER, NECK],
  },
  stormcaller: {
    rest: {
      leftUpperArm: [0.15, 0.25, -0.15],
      leftLowerArm: [1.25, -0.6],
      leftHand: [0, 0, 0],
      rightUpperArm: [0.02, 0, -0.14],
      rightLowerArm: [0.15],
      rightHand: [0, -0.9, 0],
    },
    combat: {
      spine: [0.1, 0.08],
      head: [-0.08, -0.08],
      leftUpperArm: [0.45, 0.3, 0.05],
      leftLowerArm: [1.1, -0.6],
      rightUpperArm: [0.4, 0.1, 0.12],
      rightLowerArm: [1.1],
      rightHand: [0, -0.5],
    },
    feet: [[0.02, 0.02], [0.03, -0.02]],
    combatFeet: [[0.08, 0.12], [0.1, -0.1]],
    crouch: 0.06,
    combatTwist: -0.12,
    sway: 1,
    fidgets: [ORB_GAZE, WAND_FLICK, SKY, NECK],
  },
  lightbinder: {
    rest: {
      leftUpperArm: [0.05, 0.05, -0.16],
      leftLowerArm: [0.3],
      rightUpperArm: [0.1, -0.05, -0.2],
      rightLowerArm: [1.3],
      rightHand: [-0.4, 0.15, 0],
    },
    combat: {
      spine: [0.1, 0.06],
      head: [-0.06],
      leftUpperArm: [0.45, 0.35, 0.1],
      leftLowerArm: [0.8],
      leftHand: [-0.5],
      rightUpperArm: [0.3, 0.0, -0.12],
      rightLowerArm: [1.3],
      rightHand: [-0.4, 0.1, 0],
    },
    feet: [[0.02, 0.02], [0.03, -0.02]],
    combatFeet: [[0.07, 0.1], [0.09, -0.1]],
    crouch: 0.05,
    combatTwist: -0.1,
    sway: 0.9,
    runKeep: 0.5,
    fidgets: [STAFF_LEAN, PRAY, SKY, NECK],
  },
  ranger: {
    rest: {
      leftUpperArm: [0.04, 0, -0.12],
      leftLowerArm: [0.2, 0.4],
      leftHand: [0, -0.8, -0.5],
      rightUpperArm: [0.02, 0, -0.14],
      rightLowerArm: [0.25],
    },
    combat: {
      spine: [0.14, -0.1],
      head: [-0.1, 0.1],
      leftUpperArm: [0.6, 0.1, 0.12],
      leftLowerArm: [0.45, 0.4],
      leftHand: [0, -0.8, -0.5],
      rightUpperArm: [0.5, 0.45, 0.1],
      rightLowerArm: [1.4],
    },
    feet: [[0.04, 0.02], [0.04, -0.02]],
    combatFeet: [[0.12, 0.12], [0.12, -0.1]],
    crouch: 0.08,
    combatTwist: 0.15,
    sway: 1,
    fidgets: [BOW_CHECK, SCAN, SKY, OVER_SHOULDER],
  },
};

const STYLES = new Map<string, IdleStyle>();

/** The compiled style of a class key (falls back to the default). Stable object per key. */
export function idleStyle(key: string): IdleStyle {
  const k = IDLE_STYLE_DEFS[key] ? key : 'default';
  let s = STYLES.get(k);
  if (!s) {
    const d = IDLE_STYLE_DEFS[k]!;
    s = { ...d, restV: poseVector(d.rest), combatV: poseVector(d.combat) };
    STYLES.set(k, s);
  }
  return s;
}

/** Dev tuning: recompile every style in place after editing IDLE_STYLE_DEFS (live layers pick it up). */
export function rebuildIdleStyles(): void {
  for (const [k, s] of STYLES) {
    const d = IDLE_STYLE_DEFS[k]!;
    Object.assign(s, d);
    poseVector(d.rest, s.restV);
    poseVector(d.combat, s.combatV);
  }
}

// ---------------------------------------------------------------------------
// Leg IK
// ---------------------------------------------------------------------------

/** Rig measurements (yd). */
export interface LegDims {
  thigh: number;
  shin: number;
  /** Half the distance between the hip joints. */
  hipHalf: number;
}

const C_HIPS = ch('hips', 0);
const C_SPINE = ch('spine', 0);
const C_CHEST = ch('chest', 0);
const C_NECK = ch('neck', 0);
const C_HEAD = ch('head', 0);
const C_LUA = ch('leftUpperArm', 0);
const C_RUA = ch('rightUpperArm', 0);
const C_LUL = ch('leftUpperLeg', 0);
const C_RUL = ch('rightUpperLeg', 0);
const C_LLL = ch('leftLowerLeg', 0);
const C_RLL = ch('rightLowerLeg', 0);
const C_LF = ch('leftFoot', 0);
const C_RF = ch('rightFoot', 0);

/** Channels of each arm (upper, lower, hand). */
const ARM_L = [ch('leftUpperArm', 0), ch('leftLowerArm', 0), ch('leftHand', 0)];
const ARM_R = [ch('rightUpperArm', 0), ch('rightLowerArm', 0), ch('rightHand', 0)];
const IS_ARM = new Int8Array(NCH); // 1 left, 2 right
for (const c of ARM_L) for (let a = 0; a < 3; a++) IS_ARM[c + a] = 1;
for (const c of ARM_R) for (let a = 0; a < 3; a++) IS_ARM[c + a] = 2;

const IK = { thigh: 0, spread: 0, knee: 0, foot: 0 };

/**
 * Two-bone leg IK in the clip channel space. `side` +1 right, -1 left. The hip joint moved by
 * (hx right, hy up, hz forward) yd from its rest spot, the foot target by (fx, fy, fz) from its
 * rest spot (straight leg below the hip). `pitch` / `lean` are the pelvis's own pitch (forward +)
 * and lean (right +), which the thigh angles cancel. Writes into the shared IK scratch.
 */
export function legIK(side: number, hx: number, hy: number, hz: number, fx: number, fy: number, fz: number, pitch: number, lean: number, L: LegDims): typeof IK {
  const L1 = L.thigh;
  const L2 = L.shin;
  const dx = fx - hx;
  const dy = -(L1 + L2) + fy - hy;
  const dz = fz - hz;
  const dv = -dy;
  let D = Math.sqrt(dx * dx + dy * dy + dz * dz);
  D = clamp(D, Math.abs(L1 - L2) + 1e-4, L1 + L2 - 1e-6);
  const alpha = Math.atan2(dx * side, dv);
  const beta = Math.atan2(dz, Math.sqrt(dv * dv + dx * dx));
  const gamma = Math.acos(clamp((L1 * L1 + D * D - L2 * L2) / (2 * L1 * D), -1, 1));
  const knee = Math.PI - Math.acos(clamp((L1 * L1 + L2 * L2 - D * D) / (2 * L1 * L2), -1, 1));
  IK.spread = alpha + side * lean;
  IK.thigh = beta + gamma - pitch;
  IK.knee = knee;
  IK.foot = knee - beta - gamma;
  return IK;
}

// ---------------------------------------------------------------------------
// The layer
// ---------------------------------------------------------------------------

export interface IdleInput {
  dt: number;
  /** 0..1 run blend (AnimDriver). */
  run: number;
  /** 0..1 cast blend. */
  cast: number;
  inCombat: boolean;
  stun: number;
  dead: number;
  /** Turn-in-place shuffle blend (its own leg cycle wins). */
  shuffle: number;
  /** Clip arm weights (the idle arms give way) and live clip weight (counts as combat). */
  armL: number;
  armR: number;
  clipW: number;
}

/** Seconds the combat stance lingers after combat / a clip ends. */
export const COMBAT_LINGER = 3.5;

interface Foot {
  /** Current spot (outward, forward) and the step it's on. */
  x: number;
  z: number;
  fromX: number;
  fromZ: number;
  toX: number;
  toZ: number;
  /** Step progress 0..1 (1 = planted). */
  u: number;
  lift: number;
}

const STEP_S = 0.24;
const STEP_LIFT = 0.07;

export class IdleLayer {
  /** Offsets to add to the clip channels (weighted, masked). */
  readonly out = new Float32Array(NCH);
  /** Hip (pelvis) translation, yd in the body frame. */
  hipRight = 0;
  hipUp = 0;
  hipFwd = 0;
  /** Shoulder shrug (rad, + = up) for the shoulder bones. */
  shoulder = 0;
  /** Blink weight 0..1. */
  blink = 0;
  /** Blend weights (sum 1): locomotion, relaxed idle, combat idle. */
  readonly weights = { run: 0, idle: 1, combat: 0 };

  private style: IdleStyle;
  private readonly rand: () => number;
  private readonly seedOff: number;
  private t = 0;
  // combat
  private combat = 0;
  private linger = 0;
  // breathing
  private breathPh: number;
  // weight shift
  private weightSide: number;
  private weightCur: number;
  private nextShift: number;
  // look-around
  private lookYaw = 0;
  private lookPitch = 0;
  private lookYawV = 0;
  private lookPitchV = 0;
  private lookTY = 0;
  private lookTP = 0;
  private nextLook: number;
  // blink
  private nextBlink: number;
  private blinkT = -1;
  private blinkAgain = false;
  // fidgets
  private fid: Clip | null = null;
  private fidT = 0;
  private nextFidget: number;
  private fidIndex = 0;
  private readonly fidBuf = new Float32Array(NCH);
  // feet
  private readonly feet: [Foot, Foot];
  private stepTurn = 0;

  constructor(
    styleKey: string,
    /** 0..1 per-hero phase seed. */
    seed: number,
    private readonly legs: LegDims | null,
  ) {
    this.style = idleStyle(styleKey);
    this.rand = rng(seed + 0.1234);
    this.seedOff = seed * 97.13;
    const r = this.rand;
    this.breathPh = r();
    this.weightSide = r() < 0.5 ? -1 : 1;
    this.weightCur = this.weightSide;
    this.nextShift = 3 + r() * 8;
    this.nextLook = 1 + r() * 4;
    this.nextBlink = 0.5 + r() * 3;
    this.nextFidget = 5 + r() * 9;
    this.fidIndex = Math.floor(r() * this.style.fidgets.length);
    const mk = (s: FootSpot): Foot => ({ x: s[0], z: s[1], fromX: s[0], fromZ: s[1], toX: s[0], toZ: s[1], u: 1, lift: 0 });
    this.feet = [mk(this.style.feet[0]), mk(this.style.feet[1])];
  }

  setStyle(key: string): void {
    this.style = idleStyle(key);
  }

  /** The fidget playing (debug), or null. */
  get fidgetId(): string | null {
    return this.fid?.id ?? null;
  }

  /** Start a fidget now (debug / tests). */
  playFidget(i: number): void {
    const f = this.style.fidgets;
    if (f.length === 0) return;
    this.fid = f[((i % f.length) + f.length) % f.length]!;
    this.fidT = 0;
  }

  update(inp: IdleInput): void {
    const dt = Math.max(0, Math.min(inp.dt, 0.1));
    const st = this.style;
    const r = this.rand;
    this.t += dt;
    const t = this.t;
    const o = this.out;

    // --- Blend weights ---------------------------------------------------------------------
    const alive = 1 - inp.dead;
    const busy = inp.inCombat || inp.clipW > 0.05 || inp.cast > 0.05;
    if (busy) this.linger = COMBAT_LINGER;
    else this.linger = Math.max(0, this.linger - dt);
    const wantCombat = busy || this.linger > 0 ? 1 : 0;
    this.combat = approach(this.combat, wantCombat, wantCombat ? 7 : 1.6, dt);
    const run = clamp(inp.run, 0, 1);
    const stand = 1 - run;
    const cw = smooth(this.combat);
    const w = this.weights;
    w.run = run;
    w.combat = stand * cw;
    w.idle = stand * (1 - cw);
    // Everything standing fades with death / stun; the legs also give way to the turn shuffle.
    const live = alive * (1 - 0.7 * inp.stun);
    const legW = stand * live * (1 - clamp(inp.shuffle, 0, 1));

    // --- Base pose: rest ↔ combat ---------------------------------------------------------
    const R = st.restV;
    const Cb = st.combatV;
    const keep = st.runKeep ?? 0;
    const restW = w.idle * live;
    const combW = w.combat * live;
    const armRun = run * keep * alive; // rest arms a staff keeps while running
    for (let c = 0; c < NCH; c++) {
      let v = R[c]! * restW + Cb[c]! * combW;
      const arm = IS_ARM[c]!;
      if (arm) {
        v += R[c]! * armRun;
        v *= 1 - (arm === 1 ? inp.armL : inp.armR);
      }
      o[c] = v;
    }

    // --- Fidgets (relaxed idle only) ------------------------------------------------------
    const calm = w.idle * live * (1 - inp.clipW);
    if (this.fid) {
      this.fidT += dt;
      if (this.fidT >= this.fid.duration || calm < 0.5) {
        this.fid = null;
        this.nextFidget = t + 8 + r() * 7;
      }
    } else if (t >= this.nextFidget) {
      if (calm > 0.95 && st.fidgets.length > 0) {
        this.fidIndex = (this.fidIndex + 1 + Math.floor(r() * Math.max(1, st.fidgets.length - 1))) % st.fidgets.length;
        this.playFidget(this.fidIndex);
      } else this.nextFidget = t + 2 + r() * 3;
    }
    let fidHead = 0;
    if (this.fid) {
      const F = evalClip(this.fid, this.fidT, this.fidBuf);
      const k = calm;
      for (let c = 0; c < NCH; c++) {
        const arm = IS_ARM[c]!;
        o[c] = o[c]! + F[c]! * k * (arm ? 1 - (arm === 1 ? inp.armL : inp.armR) : 1);
      }
      // Fidgets that move the head pause the look-around.
      fidHead = Math.min(1, (Math.abs(F[C_HEAD]!) + Math.abs(F[C_HEAD + 1]!) + Math.abs(F[C_HEAD + 2]!)) * 6);
    }

    // --- Breathing (chest + shoulders, asymmetric) ------------------------------------------
    const rate = 0.24 + 0.14 * cw + 0.03 * noise1(t * 0.1 + this.seedOff);
    this.breathPh = (this.breathPh + dt * rate) % 1;
    const p = this.breathPh;
    const b = (p < 0.42 ? smooth(p / 0.42) : 1 - smooth((p - 0.42) / 0.58)) - 0.5; // -0.5..0.5
    const bw = live * (1 - 0.6 * run);
    o[C_CHEST] = o[C_CHEST]! - 0.035 * b * bw;
    o[C_SPINE] = o[C_SPINE]! - 0.01 * b * bw;
    o[C_HEAD] = o[C_HEAD]! + 0.02 * b * bw;
    this.shoulder = 0.035 * b * bw;
    o[C_LUA + 2] = o[C_LUA + 2]! + 0.02 * b * bw * (1 - inp.armL);
    o[C_RUA + 2] = o[C_RUA + 2]! + 0.02 * b * bw * (1 - inp.armR);

    // --- Micro-motion (value noise, per hero) -----------------------------------------------
    const s0 = this.seedOff;
    const mw = live * stand;
    o[C_SPINE + 1] = o[C_SPINE + 1]! + 0.02 * noise1(t * 0.31 + s0) * mw;
    o[C_SPINE + 2] = o[C_SPINE + 2]! + 0.012 * noise1(t * 0.27 + s0 + 11) * mw;
    o[C_CHEST] = o[C_CHEST]! + 0.01 * noise1(t * 0.4 + s0 + 23) * mw;
    o[C_HEAD + 2] = o[C_HEAD + 2]! + 0.025 * noise1(t * 0.35 + s0 + 31) * mw;
    o[C_LUA] = o[C_LUA]! + 0.025 * noise1(t * 0.45 + s0 + 41) * mw * (1 - inp.armL);
    o[C_RUA] = o[C_RUA]! + 0.025 * noise1(t * 0.43 + s0 + 53) * mw * (1 - inp.armR);
    o[C_LUA + 2] = o[C_LUA + 2]! + 0.02 * noise1(t * 0.38 + s0 + 61) * mw * (1 - inp.armL);
    o[C_RUA + 2] = o[C_RUA + 2]! + 0.02 * noise1(t * 0.36 + s0 + 67) * mw * (1 - inp.armR);

    // --- Look-around (neck + head, critically damped) ---------------------------------------
    if (t >= this.nextLook) {
      const center = r() < 0.35;
      const prevY = this.lookTY;
      this.lookTY = center ? 0 : (r() * 2 - 1) * 0.55;
      this.lookTP = center ? 0 : -0.04 + (r() * 2 - 1) * 0.1;
      this.nextLook = t + 2.2 + r() * 4.5;
      if (Math.abs(this.lookTY - prevY) > 0.3 && this.blinkT < 0) this.blinkT = 0; // blink on a big glance
    }
    const lookScale = (1 - fidHead) * (1 - 0.7 * cw) * live * stand;
    const K = 26;
    const Cd = 2 * Math.sqrt(K) * 0.95;
    this.lookYawV += (K * (this.lookTY * lookScale - this.lookYaw) - Cd * this.lookYawV) * dt;
    this.lookYaw += this.lookYawV * dt;
    this.lookPitchV += (K * (this.lookTP * lookScale - this.lookPitch) - Cd * this.lookPitchV) * dt;
    this.lookPitch += this.lookPitchV * dt;
    o[C_NECK + 1] = o[C_NECK + 1]! + 0.38 * this.lookYaw;
    o[C_HEAD + 1] = o[C_HEAD + 1]! + 0.62 * this.lookYaw;
    o[C_CHEST + 1] = o[C_CHEST + 1]! + 0.12 * this.lookYaw;
    o[C_NECK] = o[C_NECK]! + 0.4 * this.lookPitch;
    o[C_HEAD] = o[C_HEAD]! + 0.6 * this.lookPitch;

    // --- Blink ---------------------------------------------------------------------------
    if (this.blinkT < 0 && t >= this.nextBlink) this.blinkT = 0;
    if (this.blinkT >= 0) {
      this.blinkT += dt;
      const u = this.blinkT / 0.15;
      this.blink = u < 1 ? Math.sin(u * Math.PI) : 0;
      if (u >= 1) {
        this.blinkT = -1;
        if (!this.blinkAgain && r() < 0.18) {
          this.blinkAgain = true;
          this.nextBlink = t + 0.12;
        } else {
          this.blinkAgain = false;
          this.nextBlink = t + 2 + r() * 4;
        }
      }
    } else this.blink = 0;

    // --- Weight shift (contrapposto) ---------------------------------------------------------
    if (t >= this.nextShift) {
      if (calm > 0.6) this.weightSide = -this.weightSide;
      this.nextShift = t + 6 + r() * 8;
    }
    this.weightCur = approach(this.weightCur, this.weightSide, 1.6, dt);
    const ws = this.weightCur * st.sway * w.idle; // + = on the right leg
    const legs = this.legs;
    const hipHalf = legs ? legs.hipHalf : 0.1;
    // Pelvis: shift over the weight leg, the free hip drops (lean toward the free side).
    const tilt = -0.06 * ws;
    const twist = st.combatTwist * w.combat + 0.04 * ws;
    const bounce = (st.bounce ?? 0) * w.combat * Math.sin(t * Math.PI * 2 * (st.bounceHz ?? 1.2) + s0);
    const hipR = 0.05 * ws;
    const hipU = -(hipHalf * Math.abs(Math.sin(tilt)) + 0.006 * Math.abs(ws)) - st.crouch * w.combat - Math.abs(bounce);
    const hipF = -0.01 * w.combat;
    const hipPitch = 0.05 * w.combat;
    o[C_HIPS] = o[C_HIPS]! + hipPitch * legW;
    o[C_HIPS + 1] = o[C_HIPS + 1]! + twist * legW;
    o[C_HIPS + 2] = o[C_HIPS + 2]! + tilt * legW;
    // Upper body counters the pelvis (shoulders tilt the other way, the head stays level, the chest faces front).
    o[C_SPINE + 2] = o[C_SPINE + 2]! + 0.085 * ws * legW;
    o[C_CHEST + 2] = o[C_CHEST + 2]! + 0.025 * ws * legW;
    o[C_HEAD + 2] = o[C_HEAD + 2]! - 0.03 * ws * legW;
    o[C_SPINE + 1] = o[C_SPINE + 1]! - twist * 0.6 * legW;
    o[C_CHEST + 1] = o[C_CHEST + 1]! - twist * 0.3 * legW;
    o[C_HEAD + 1] = o[C_HEAD + 1]! - twist * 0.1 * legW;
    this.hipRight = hipR * legW;
    this.hipUp = hipU * legW;
    this.hipFwd = hipF * legW;

    // --- Feet: step into the stance, then plant with IK -------------------------------------
    const cf = this.combat > 0.5;
    const spots = cf ? st.combatFeet : st.feet;
    if (stand < 0.5 || legW < 0.05) {
      // Moving / turning: the run cycle owns the legs; land straight in the current stance.
      for (let i = 0; i < 2; i++) {
        const f = this.feet[i]!;
        f.x = f.toX = spots[i]![0];
        f.z = f.toZ = spots[i]![1];
        f.u = 1;
        f.lift = 0;
      }
    } else {
      // One foot at a time, alternating.
      const stepping = this.feet[0].u < 1 || this.feet[1].u < 1;
      if (!stepping) {
        for (let k = 0; k < 2; k++) {
          const i = (this.stepTurn + k) % 2;
          const f = this.feet[i]!;
          const sx = spots[i]![0];
          const sz = spots[i]![1];
          if (Math.abs(sx - f.x) + Math.abs(sz - f.z) > 0.015) {
            f.fromX = f.x;
            f.fromZ = f.z;
            f.toX = sx;
            f.toZ = sz;
            f.u = 0;
            this.stepTurn = 1 - i;
            break;
          }
        }
      }
      for (let i = 0; i < 2; i++) {
        const f = this.feet[i]!;
        if (f.u >= 1) {
          f.lift = 0;
          continue;
        }
        f.u = Math.min(1, f.u + dt / STEP_S);
        const e = smooth(f.u);
        f.x = f.fromX + (f.toX - f.fromX) * e;
        f.z = f.fromZ + (f.toZ - f.fromZ) * e;
        f.lift = Math.sin(f.u * Math.PI) * STEP_LIFT;
      }
    }
    if (legs) {
      const tw = twist * legW;
      const tl = tilt * legW;
      const pitch = o[C_HIPS]!;
      for (let i = 0; i < 2; i++) {
        const side = i === 0 ? -1 : 1; // left, right
        const f = this.feet[i]!;
        // Hip joint displacement: the pelvis shift plus its tilt / twist about the centre.
        const hx = this.hipRight;
        const hy = this.hipUp - side * hipHalf * Math.sin(tl);
        const hz = this.hipFwd + side * hipHalf * Math.sin(tw);
        const ik = legIK(side, hx, hy, hz, side * f.x * legW, f.lift * legW, f.z * legW, pitch, tl, legs);
        const free = side === 1 ? Math.max(0, -ws) : Math.max(0, ws); // this leg carries no weight
        const cul = i === 0 ? C_LUL : C_RUL;
        const cll = i === 0 ? C_LLL : C_RLL;
        const cft = i === 0 ? C_LF : C_RF;
        o[cul] = o[cul]! + ik.thigh + 0.05 * free * legW;
        o[cul + 2] = o[cul + 2]! + ik.spread;
        o[cll] = o[cll]! + ik.knee + 0.12 * free * legW;
        o[cft] = o[cft]! + ik.foot - 0.06 * free * legW;
      }
    }
  }
}
